import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

type PromiseResolver<T> = {
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
};

export interface UseDebouncedAsyncSelectOptions<T> {
  fetchFn: (query: string) => Promise<T[]>;
  getEmptyResult: () => T[];
  debounceMs?: number;
  minQueryLength?: number;
}

export interface UseDebouncedAsyncSelectReturn<T> {
  options: T[];
  setOptions: React.Dispatch<React.SetStateAction<T[]>>;
  loadOptions: (query: string) => Promise<T[]>;
  isLoading: boolean;
  error: Error | null;
  clearError: () => void;
}

/**
 * Custom hook for debounced async select with promise batching.
 *
 * Features:
 * - Debounces search queries to reduce API calls
 * - Batches multiple rapid calls into single request
 * - Resolves all pending promises with same result
 * - Proper cleanup on unmount to prevent memory leaks
 * - Loading and error state management
 * - Configurable minimum query length
 *
 * @example
 * ```tsx
 * const { options, loadOptions, isLoading, error } = useDebouncedAsyncSelect({
 *   fetchFn: (query) => api.search(query),
 *   getEmptyResult: () => [],
 *   debounceMs: 300, // Optional, defaults to 300ms
 *   minQueryLength: 2 // Optional, defaults to 1
 * });
 * ```
 */
export function useDebouncedAsyncSelect<T>({
  fetchFn,
  getEmptyResult,
  debounceMs = 300,
  minQueryLength = 1,
}: UseDebouncedAsyncSelectOptions<T>): UseDebouncedAsyncSelectReturn<T> {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, debounceMs);
  const [options, setOptions] = useState<T[]>(getEmptyResult);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const resolversRef = useRef<PromiseResolver<T[]>[]>([]);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch options when debounced query changes
  useEffect(() => {
    const query = debouncedQuery.trim();
    const currentResolvers = resolversRef.current;
    resolversRef.current = [];

    // Handle empty query or query shorter than minimum length
    if (!query || query.length < minQueryLength) {
      const emptyResult = getEmptyResult();
      setOptions(emptyResult);
      setIsLoading(false);
      setError(null);
      currentResolvers.forEach(({ resolve }) => resolve(emptyResult));
      return;
    }

    // Set loading state and clear previous error
    setIsLoading(true);
    setError(null);

    // Fetch and resolve all batched promises with same result
    fetchFn(query)
      .then((result) => {
        setOptions(result);
        setIsLoading(false);
        currentResolvers.forEach(({ resolve }) => resolve(result));
      })
      .catch((error) => {
        const errorInstance = error instanceof Error ? error : new Error(String(error));
        setError(errorInstance);
        setIsLoading(false);
        currentResolvers.forEach(({ reject }) => reject(errorInstance));
      });
  }, [debouncedQuery, fetchFn, getEmptyResult, minQueryLength]);

  // Cleanup pending promises on unmount
  useEffect(() => {
    return () => {
      const pendingResolvers = resolversRef.current;
      resolversRef.current = [];

      if (pendingResolvers.length > 0) {
        const cancelError = new Error('Component unmounted');
        pendingResolvers.forEach(({ reject }) => reject(cancelError));
      }
    };
  }, []);

  /**
   * Load options for given query.
   * For empty queries or queries shorter than minimum length, returns immediately.
   * For valid queries, batches the promise to be resolved after debounce.
   */
  const loadOptions = useCallback(
    (query: string) => {
      setSearchQuery(query);

      // Return immediately for empty query or query shorter than minimum length
      const trimmedQuery = query.trim();
      if (!trimmedQuery || trimmedQuery.length < minQueryLength) {
        return Promise.resolve(getEmptyResult());
      }

      // Queue promise to be resolved when fetch completes
      return new Promise<T[]>((resolve, reject) => {
        resolversRef.current.push({ resolve, reject });
      });
    },
    [getEmptyResult, minQueryLength]
  );

  return { 
    options, 
    setOptions, 
    loadOptions, 
    isLoading, 
    error, 
    clearError 
  };
}